/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    /** @type {HTMLDivElement | null} Wrapper for the injected product root. */
    content = null
    /** @type {HTMLElement | null} Drawer footer that holds <product-form> / fallback markup. */
    footer = null
    /** @type {HTMLElement | null} The <product-form> wrapper inside the footer. */
    form = null
    /** @type {HTMLElement | null} "Added to cart" toast inside the drawer. */
    notification = null
    /** @type {HTMLButtonElement | null} Back button shown after navigating away from home. */
    backBtn = null
    /** @type {NodeListOf<HTMLAnchorElement>} */
    openCartDrawerLinks
    /** @type {HTMLElement | null} The shared <cart-drawer> instance, if any. */
    cartDrawer = null
    /** @type {{ url: string, promise: Promise<Response> } | null} In-flight or pre-fetched request. */
    fetch = null
    /** @type {string[]} Already-prefetched product URLs (used to dedupe hover prefetch). */
    fetchedUrls = []
    /** @type {string | undefined} URL of the product currently shown in the drawer. */
    productUrl = undefined
    /** @type {MutationObserver | null} */
    observer = null


    constructor() {
      super();
      this.content = /** @type {HTMLDivElement | null} */ (this.querySelector('.js-product-details'));
      this.footer = /** @type {HTMLElement | null} */ (this.querySelector('.drawer__footer'));
      this.form = /** @type {HTMLElement | null} */ (this.querySelector('product-form'));
      this.notification = /** @type {HTMLElement | null} */ (this.querySelector('.js-added-to-cart'));
      this.backBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.drawer__back-btn'));
      this.openCartDrawerLinks = /** @type {NodeListOf<HTMLAnchorElement>} */ (
        this.querySelectorAll('.js-open-cart-drawer')
      );
      this.cartDrawer = /** @type {HTMLElement | null} */ (document.querySelector('cart-drawer'));
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener(
        theme.events.variantChange,
        /** @type {EventListener} */(
          /** @param {CustomEvent<VariantChangeEventDetail>} e */ (e) => this.handleVariantChange(e)
        )
      );

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
       /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll('.js-quick-add')).forEach(button => {
        this.bindQuickAddButtonMouseEnter(button);
      });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback () {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', /** @type {EventListener} */(this.quickAddButtonMouseEnterHandler));
      });
      if (this.observer) this.observer.disconnect();
      // Drop any in-flight fetch so a later open() doesn't try to
      // await a promise that was started before disconnect.
      this.fetch = null;
    }

    /**
     * Bind a `mouseenter` handler to a `.js-quick-add` button. Idempotent —
     * the same handler reference is reused and a `data-` flag is set on the
     * element so a second call is a no-op.
     * @param {HTMLElement} button
     */
    bindQuickAddButtonMouseEnter (button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Kick off a product-page prefetch on hover. Stored on `this.fetch` so
     * the subsequent `open()` can await the same promise without firing a
     * second request. Deduped by URL.
     * @param {MouseEvent} evt
     */
    handleQuickAddButtonMouseEnter (evt) {
      const target = /** @type {HTMLElement} */ (evt.target);
      const productUrl = target.dataset.productUrl;
      if (!productUrl) return;
      if (!this.fetchedUrls.includes(productUrl)) {
        this.fetch = {
          url: productUrl,
          promise: fetch(productUrl)
        };
        this.fetchedUrls.push(productUrl);
      }
    }

    /**
     * Click handler for the "View cart" success link inside the drawer.
     * @param {MouseEvent} [evt]
     */
    handleOpenCartClick (evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt?.preventDefault();
        // @ts-ignore
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt?.preventDefault();
        this.close();
      }
    }

    /**
     * Document-level click handler — any `.js-quick-add` click opens this
     * drawer, closing the cart drawer first if it is currently shown.
     * @param {MouseEvent} evt
     */
    handleDocumentClick (evt) {
      const target = /** @type {Element} */ (evt.target);
      if (!target.matches?.('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        /** @type {HTMLDivElement|null} */
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';
        // @ts-ignore
        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          if (this.backBtn) this.backBtn.hidden = false;
          this.open(/** @type {HTMLElement} */(target));
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(/** @type {HTMLElement} */(target));
      }
    }

    /** @param {string} url */
    updateLinks (url) {
      /** @type {NodeListOf<HTMLLinkElement>} */ (this.querySelectorAll('.js-prod-link')).forEach((link) => {
      link.href = url;
    });
    }

    /**
     * Sync every `.js-prod-link` in the drawer to the currently-selected
     * variant's deep link.
     * @param {CustomEvent<VariantChangeEventDetail>} evt
     */
    handleVariantChange (evt) {
      const baseUrl = this.productUrl || '';
      let url = baseUrl;

      if (evt.detail?.variant) {
        const separator = baseUrl ? baseUrl.split('?').length > 1 ? '&' : '?' : '?';
        url = `${baseUrl}${separator}variant=${evt.detail.variant.id}`;
      }
      this.updateLinks(url)

    }

    /**
     * Open the drawer for the given trigger button. Reuses the prefetched
     * HTML response if one is already in flight, otherwise issues a new
     * `fetch`.
     * @param {HTMLElement} opener
     * @returns {Promise<void>}
     */
    async open (opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }
      if (!this.content) return
      this.productUrl = opener.dataset.productUrl;
      // 同步抽屉头部的 "view details" 链接（首次打开 / 切换产品时）
      this.updateLinks(this.productUrl || '')
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      if (this.content) this.content.classList.add('drawer__content--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        const url = /** @type {string} */ (opener.dataset.productUrl);
        this.fetch = {
          url,
          promise: fetch(url)
        };
      }

      let response;
      try {
        response = await this.fetch.promise;
        if (!response.ok) throw new Error(response.statusText);
        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.insertHTML(responseHTML, opener);
      } catch (err) {
        console.error('[quick-add] failed to load product', err);
        this.content.innerHTML = `
          <div class="alert bg-error-bg text-error-text p-4 text-sm">
            Failed to load product. Please try again.
          </div>
        `;
        this.classList.remove('is-loading');
        this.content.classList.remove('drawer__content--out');
      } finally {
        opener.removeAttribute('aria-disabled');
      }

      this.fetch = null;
      window.initLazyImages();

    }

    /**
     * Close the drawer and hide the back button.
     */
    close () {
      super.close(() => {
        if (this.backBtn) this.backBtn.hidden = true;
      });
    }


    /**
     * Pre-select the colour the user clicked on in the source product card.
     * Supports both swatch buttons (`.opt-btn`) and dropdown options
     * (`.custom-select__option`).
     * @param {HTMLElement} opener
     */
    setActiveVariant (opener) {
      const colorOptionBox = /** @type {HTMLElement | null} */ (
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
      );
      if (colorOptionBox) {
        colorOptionBox.click();
        return;
      }
      const colorOptionDropdown = this.querySelector(
        `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
      );
      if (colorOptionDropdown) {
        const customSelect = /** @type {HTMLElement & { selectOption: (el: Element) => void }} */ (
          colorOptionDropdown.closest('custom-select')
        );
        if (customSelect) customSelect.selectOption(colorOptionDropdown);
      }
    }




    /**
     * Move the freshly-injected <product-form> out of the product root and
     * into the drawer footer. The element stays connected the whole time
     * (it never leaves the DOM), so its custom-element upgrade runs only
     * once and we only call `init()` once.
     */
    mountProductForm () {
      if (!this.footer || !this.form) return;
      this.footer.classList.remove('quick-add__footer-message');
      this.footer.classList.remove('drawer__footer--out');

      const incoming = this.content?.querySelector('product-form');
      if (incoming) {
        this.form.innerHTML = incoming.innerHTML
        incoming.remove()
        // @ts-ignore
        if (typeof this.form.init === 'function') this.form.init();
        // @ts-ignore
        if (Shopify?.PaymentButton?.init) Shopify.PaymentButton.init();

        return;
      }

      // Fallbacks for products without a <product-form> (pre-order / awaiting-sale).
      const signUpForm = this.content?.querySelector('.product-signup');
      if (signUpForm) {
        this.form.innerHTML = signUpForm.innerHTML;
        return;
      }

      this.footer.classList.add('quick-add__footer-message');
      this.form.innerHTML = `
        <div class="alert quick-add__alert bg-info-bg text-info-text">
          <div class="flex">
            <div>
              <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
              </svg>
            </div>
            <div>
              <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
              <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
            </div>
          </div>
        </div>`;
    }



    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart () {
      this.handleOpenCartClick()
      // if (this.notification) {
      //   setTimeout(() => {
      //     this.notification.hidden = false;
      //   }, 300);

      //   setTimeout(() => {
      //     this.notification.hidden = true;
      //   }, this.notification.dataset.visibleFor);
      // }
    }
    /**
     * Inject the rendered `main-product.liquid` HTML into the drawer.
     * Strips everything that doesn't belong inside a drawer (fixed
     * elements, JSON-LD, scripts, inline styles) and rewrites ids to
     * avoid collisions with the host page.
     * @param {Document} responseHTML - Parsed response document.
     * @param {HTMLElement} opener - Element that triggered opening.
     */
    insertHTML = (responseHTML, opener) => {
      if (!this.content) return
      // Locate the <main-product> root. Fall back to .product-main
      // (the section class on main-product.liquid) in case the
      // custom element tag was rewritten or missing.
      /** @type {Element | null} */
      const root =
        responseHTML.querySelector('.product-main') ||
        responseHTML.querySelector('.js-product') ||
        responseHTML.querySelector('main-product');
      if (!root || !this.content) {
        this.content.innerHTML = `
          <div class="alert bg-error-bg text-error-text p-4 text-sm">
            Failed to load product. Please try again.
          </div>
        `;
        this.classList.remove('is-loading');
        this.content.classList.remove('drawer__content--out');
        return;
      }

      const cleanRoot = this.extractProductRoot(root);
      this.content.appendChild(cleanRoot);


      window.initLazyImages();
      theme.utils.execDomScript(this.content);
      // theme.utils.execDomStylesheet(this.content)

      // customElements upgrade synchronously fires once the node is
      // connected. queueMicrotask gives the upgrade callbacks a chance
      // to run before we try to set the active variant.
      queueMicrotask(() => {
        if (opener && opener.dataset.selectedColor) this.setActiveVariant(opener);
        // Move <product-form> from the product root into the drawer's footer.
        // Done here (after upgrade) so the element is already a real
        // ProductForm instance and we only need to call init() once.
        this.mountProductForm();
        this.classList.remove('is-loading');
        if (this.content) this.content.classList.remove('drawer__content--out');
      });
    }

    /**
     * Deep-clone the main-product root, strip everything that
     * doesn't belong inside a drawer, and rewrite ids to avoid
     * collisions with the host page (e.g. when the drawer is
     * opened from a product page that already has its own
     * main-product).
     * @param {Element} root - The <main-product> or .product-main element.
     * @returns {Element} Cleaned clone, ready to be appended.
     */
    extractProductRoot (root) {
      const clone = /** @type {HTMLDivElement} */(root.cloneNode(true));
      const firstChild = clone.querySelector('.container');
      if (firstChild) {
        firstChild.classList.remove('container');
      }
      // Some quick-add triggers are cards on the same product page;
      // strip any duplicate section id that would clash with the
      // host page (main-product.liquid renders data-section="{{ section.id }}").
      /** @type {HTMLDivElement | null} */
      const productSection = clone.querySelector('.product-section');
      if (productSection) {
        // remove .js-product for variant-picker query section
        // productSection.classList.remove("js-product")
        const sectionId = productSection.dataset && productSection.dataset.section;
        if (sectionId) {
          clone.querySelectorAll(`[id$="-${sectionId}"]`).forEach((el) => {
            // 用 getAttribute 而非 .id — SVG 元素的 .id 是 SVGAnimatedString，没有 .replace
            const currentId = el.getAttribute('id');
            if (currentId) el.setAttribute('id', currentId.replace(`-${sectionId}`, '-quickadd'));
          });
        }
      }
      clone.querySelectorAll('variant-picker').forEach((vp) => {
        // @ts-ignore
        vp.dataset.updateUrl = 'false';
        // @ts-ignore
        vp.dataset.inQuickAddDrawer = 'true';
      });
      // Generic id collisions regardless of section id.
      const idRewrites = {
        'product-media': 'product-media-qa',
        'product-info': 'product-info-qa',
        'variants': 'variants-qa',
      };
      Object.entries(idRewrites).forEach(([from, to]) => {
        const el = clone.querySelector(`#${from}`);
        if (el) el.id = to;
      });
      // Sync href anchors that point at the rewritten ids.
      // clone.querySelectorAll('a[href^="#"]').forEach((a) => {
      //   const href = a.getAttribute('href');
      //   if (href && idRewrites[href.slice(1)]) a.setAttribute('href', `#${idRewrites[href.slice(1)]}`);
      // });


      clone.querySelectorAll('product-model, .deferred-media').forEach((el) => {
        el.innerHTML = '';
        el.removeAttribute('class');
      });
      // Mark the root so CSS (`.quick-add-drawer .quick-add-product`)
      // and JS debugging can find it.
      clone.classList.add('quick-add-product');
      return clone;
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
